import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress
} from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import { useEffect, useState } from "react";
import useLocales from "../../hooks/useLocales";
import { axiosPrivate } from "../../services/axios";
import AllocationAPI from "../../services/AllocationService";
import { anchorOrigin } from "../../utils/constants";
import { useSnackbar } from "notistack";
import {
  createDateForPicker,
  formatToYYYYMMDD
} from "../../utils/customC2CFormat";
import { getDeletePlacementErrorMessage } from "../../utils/placementErrroMessages";

export const DeleteAllocationModal = ({
  onClose,
  allocationId,
  allocationList,
  setRefreshCount,
  row
}) => {
  // Today's date as YYYY-MM-DD
  const today = new Date();
  const todayStr = today.toISOString().slice(0, 10);

  useEffect(() => {
    if (row?.start_date && row.start_date > todayStr) {
      setSelectedDate(todayStr);
      setAutoDeleted(true);
      // Use async IIFE to call handleDelete after state updates
      (async () => {
        await handleDeleteAuto(todayStr);
      })();
    }
    // eslint-disable-next-line
  }, [row?.start_date]);

  // Separated handler for auto-delete to avoid blocking UI
  const handleDeleteAuto = async (dateStr) => {
    setLoading(true);
    try {
      const payload = {
        end_date: dateStr,
        delete_submitted: shouldDeleteSubmittedTs
      };
      await AllocationAPI.DELETE(axiosPrivate, allocationId, payload);
      setRefreshCount((current) => current + 1);
      enqueueSnackbar("Allocation deleted successfully", {
        variant: "success",
        anchorOrigin
      });
      onClose();
    } catch (error) {
      console.error("Error deleting allocation:", error);
      setAllocationError(error.response?.data || {});
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };
  const { enqueueSnackbar } = useSnackbar();
  const { translate } = useLocales();
  const [selectedDate, setSelectedDate] = useState(null);
  const [autoDeleted, setAutoDeleted] = useState(false);
  const [shouldDeleteSubmittedTs, setShouldDeleteSubmittedTs] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [allocationError, setAllocationError] = useState(null);

  const handleDelete = async () => {
    if (!selectedDate) return;
    setLoading(true);
    try {
      const payload = {
        end_date: formatToYYYYMMDD(selectedDate),
        delete_submitted: shouldDeleteSubmittedTs
      };
      await AllocationAPI.DELETE(axiosPrivate, allocationId, payload);
      setRefreshCount((current) => current + 1);
      enqueueSnackbar("Allocation deleted successfully", {
        variant: "success",
        anchorOrigin
      });
      onClose();
    } catch (error) {
      console.error("Error deleting allocation:", error);
      setAllocationError(error.response?.data || {});
      setShowErrorModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleConfirm = (confirm) => {
    if (confirm) {
      setShouldDeleteSubmittedTs(true);
      handleDelete();
    } else {
      setShowErrorModal(false);
    }
  };

  // If auto-deleting, do not render the modal
  if (autoDeleted) return null;

  return (
    <>
      <Dialog
        open={true}
        onClose={onClose}
        sx={{
          "& .MuiDialog-container": {
            alignItems: "flex-start"
          },
          "& .MuiPaper-root": {
            marginTop: "10vh"
          }
        }}
      >
        <DialogTitle>
          {translate("Select End Date To Delete This Allocation")}
        </DialogTitle>
        <DialogContent>
          <DatePicker
            label="End Date"
            value={createDateForPicker(selectedDate)}
            onChange={(newValue) => setSelectedDate(newValue)}
            minDate={createDateForPicker(row?.start_date)}
            maxDate={createDateForPicker(row?.end_date)}
            inputFormat="yyyy-MM-dd"
            renderInput={(params) => (
              <TextField
                {...params}
                fullWidth
                margin="dense"
                placeholder="yyyy-mm-dd"
              />
            )}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="secondary" variant="outlined">
            {translate("Cancel")}
          </Button>
          <Button
            disabled={!selectedDate || loading}
            variant="contained"
            onClick={handleDelete}
            startIcon={
              loading ? <CircularProgress size={20} color="inherit" /> : null
            }
          >
            {loading ? translate("Deleting...") : translate("Delete")}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showErrorModal}
        onClose={() => handleConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{
            textAlign: "center",
            fontWeight: 600,
            color: "text.secondary",
            pb: 1
          }}
        >
          {allocationError?.error_code === "DEL_002"
            ? "Employee has already submitted timesheets"
            : "This action cannot be completed. Please read the details and make the necessary changes accordingly."}
        </DialogTitle>
        <DialogContent
          sx={{
            textAlign: "center",
            py: 2,
            px: 3,
            fontSize: "1rem",
            color: "text.primary"
          }}
        >
          {getDeletePlacementErrorMessage(allocationError?.error_code)}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3 }}>
          {allocationError?.error_code === "DEL_002" ? (
            <>
              <Button
                onClick={() => handleConfirm(false)}
                color="secondary"
                variant="outlined"
                sx={{ px: 3, py: 1 }}
              >
                No
              </Button>
              <Button
                onClick={() => handleConfirm(true)}
                color="primary"
                variant="contained"
                sx={{ px: 3, py: 1 }}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
                disabled={loading}
              >
                {loading ? "Deleting..." : "Yes"}
              </Button>
            </>
          ) : (
            <Button
              onClick={() => handleConfirm(false)}
              color="primary"
              variant="contained"
              sx={{ px: 3, py: 1 }}
            >
              Close
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </>
  );
};
